import { useEffect, useCallback, useRef, useState, useId } from "react";
import { useSearchStore, PAGE_SIZE, SearchFormData } from "../../../store/searchStore";
import {
  fetchPins,
  searchProperties,
  MapBounds,
  MapPin,
  PropertyResult,
} from "../../../services/api";
import { Sort } from "../../../types/constants";
import styles from "./index.module.scss";
import PropertyCard from "./PropertyCard";

const sortLabels: Record<Sort, string> = {
  [Sort.Newest]: "Newest",
  [Sort.PriceDescending]: "Price: High to Low",
  [Sort.PriceAscending]: "Price: Low to High",
};

/** "properties" rather than "homes": land and commercial are in the inventory too. */
function formatCount(n: number): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? "property" : "properties"}`;
}

/** How long after the last filter edit before we auto-run the search. */
const FILTER_DEBOUNCE_MS = 450;

/** Fallback pins when the Pins call fails: whatever page 0 can plot. */
function pinsFromResults(results: PropertyResult[]): MapPin[] {
  return results
    .filter((p) => p.latitude && p.longitude)
    .map((p) => ({
      id: p.id,
      lat: p.latitude as number,
      lng: p.longitude as number,
      price: p.price,
    }));
}

/**
 * Pins depend on the filters only: never on sort (fetchPins drops it) and
 * never on the map area (pins are always the whole filter set). A search
 * whose key matches the pins on screen skips the Pins call.
 */
function pinsKeyFor(formData: SearchFormData): string {
  return JSON.stringify({ ...formData, sort: undefined });
}

function SkeletonCard() {
  return (
    <div className={styles.skeletonCard} aria-hidden="true">
      <div className={styles.skeletonImage} />
      <div className={styles.skeletonBody}>
        <div className={`${styles.skeletonLine} ${styles.skeletonLineWide}`} />
        <div className={styles.skeletonLine} />
        <div className={`${styles.skeletonLine} ${styles.skeletonLineNarrow}`} />
      </div>
    </div>
  );
}

/* MLS/IDX disclaimer: collapsed to a pill by default, expanding to a popover
   anchored above it. Required attribution stays one click away.

   Rendered once, outside the scrolling list: a collapsed pill is only useful
   if it is always on screen. Desktop floats it over the bottom of the panel;
   mobile gives it a row under the results header so it never covers a card. */
function Disclaimer() {
  const [open, setOpen] = useState(false);
  const barRef = useRef<HTMLElement>(null);
  const popoverId = `idx-disclaimer-${useId()}`;

  // Dismiss on Escape or a click outside.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointerDown = (e: MouseEvent) => {
      if (!barRef.current?.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  return (
    <footer className={styles.disclaimerBar} ref={barRef}>
      {open && (
        <div className={styles.disclaimerPopover} id={popoverId} role="group">
          <div className={styles.disclaimerHead}>
            <span className={styles.disclaimerTitle}>MLS / IDX disclaimer</span>
            <button
              type="button"
              className={styles.disclaimerClose}
              onClick={() => setOpen(false)}
              aria-label="Minimize disclaimer"
            >
              &times;
            </button>
          </div>
          <p className={styles.disclaimerText}>
            &copy; {new Date().getFullYear()} Teton Board of REALTORS&reg; and Snake River Regional
            Multiple Listing Service. Information provided through the Teton IDX Program is provided
            for consumer&rsquo;s personal, non-commercial use and may not be used for any purpose
            other than to identify prospective properties and individual consumers interested in
            purchasing. Properties displayed through the Teton and Snake River IDX Programs may be
            selected by and displayed by an IDX participant, who may not necessarily be the listing
            agent. Any information contained herein is deemed reliable, but not guaranteed and the
            Teton &amp; Snake River MLS are not responsible or liable for the information. Teton
            Valley Realty does not guarantee the completeness or accuracy and shall not be liable
            for this information.
          </p>
        </div>
      )}

      <button
        type="button"
        className={styles.disclaimerToggle}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={popoverId}
      >
        <span className={styles.disclaimerToggleIcon} aria-hidden="true">
          i
        </span>
        Disclaimer
      </button>
    </footer>
  );
}

export default function Results() {
  const formData = useSearchStore((s) => s.formData);
  const searchVersion = useSearchStore((s) => s.searchVersion);
  const isLoading = useSearchStore((s) => s.isLoading);
  const isLoadingMore = useSearchStore((s) => s.isLoadingMore);
  const searchError = useSearchStore((s) => s.searchError);
  const results = useSearchStore((s) => s.results);
  const totalResults = useSearchStore((s) => s.totalResults);
  const pinsTotal = useSearchStore((s) => s.pinsTotal);
  const resultsScoped = useSearchStore((s) => s.resultsScoped);
  const hasMore = useSearchStore((s) => s.hasMore);
  const activePropertyId = useSearchStore((s) => s.activePropertyId);
  const setResults = useSearchStore((s) => s.setResults);
  const appendResults = useSearchStore((s) => s.appendResults);
  const setPins = useSearchStore((s) => s.setPins);
  const setLoading = useSearchStore((s) => s.setLoading);
  const setLoadingMore = useSearchStore((s) => s.setLoadingMore);
  const setLoadingPins = useSearchStore((s) => s.setLoadingPins);
  const setSearchError = useSearchStore((s) => s.setSearchError);
  const setField = useSearchStore((s) => s.setField);
  const submitSearch = useSearchStore((s) => s.submitSearch);
  const clearMapArea = useSearchStore((s) => s.clearMapArea);

  const listRef = useRef<HTMLDivElement>(null);
  const countRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const loadMoreAbortRef = useRef<AbortController | null>(null);
  // Query behind the list on screen. Infinite scroll pages with this, not the
  // live store, so a pan or filter edit still inside its debounce can't splice
  // a page of a different search onto this one.
  const pageQueryRef = useRef<{ formData: SearchFormData; bounds?: MapBounds } | null>(null);
  // Filters behind the pins on screen (see pinsKeyFor).
  const pinsKeyRef = useRef("");

  // ---- New search: first page, plus the pin set when it changed --------
  // Every search runs through here via searchVersion (filters, AI, a map
  // pan). The list is scoped to mapBounds once the visitor has moved the map;
  // the pins never are, so a pan never empties the map and costs one request.
  const runSearch = useCallback(async () => {
    abortRef.current?.abort();
    // A page still loading for the previous query must not land on this one.
    loadMoreAbortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Read filters + bounds from the store at call time rather than from the
    // render closure. searchVersion bumps (URL restore, a map pan, AI query,
    // debounced filter edits) all run this callback, and the triggering
    // state change isn't guaranteed to have propagated into this component's
    // closure yet: reading getState() guarantees we always fetch the latest
    // filter set, so a saved-search link applies its params on first load.
    const { formData, mapBounds, fitPending, pinsTotal } = useSearchStore.getState();
    const bounds = mapBounds ?? undefined;
    pageQueryRef.current = { formData, bounds };

    // Refetch pins only when the filters behind them changed, a re-frame is
    // waiting on fresh pins (setPins is what hands the fit to the map), or
    // the last Pins call failed and the map is plotting the page-0 fallback.
    // A pan or a sort change reuses them.
    const pinsKey = pinsKeyFor(formData);
    const needPins = pinsKey !== pinsKeyRef.current || fitPending || pinsTotal < 0;

    setLoading(true);
    if (needPins) setLoadingPins(true);
    setSearchError(false);
    try {
      // Pins are best-effort: a failure degrades to plotting page 0 rather
      // than failing the whole search.
      const [page, pinsRes] = await Promise.all([
        searchProperties(formData, PAGE_SIZE, 0, bounds, true, controller.signal),
        needPins
          ? fetchPins(formData, undefined, controller.signal).catch((err) => {
              if ((err as Error).name === "AbortError") throw err;
              console.error("Pins failed:", err);
              return null;
            })
          : null,
      ]);
      if (controller.signal.aborted) return;

      setResults(page.results, page.total, !!bounds);
      if (needPins) {
        if (pinsRes) {
          setPins(pinsRes.pins, pinsRes.total);
          pinsKeyRef.current = pinsKey;
        } else {
          setPins(pinsFromResults(page.results), -1);
        }
      }

      // New result set: jump the list back to the top.
      if (listRef.current) listRef.current.scrollTop = 0;
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      console.error("Search failed:", err);
      setResults([], 0);
      // A failed pan keeps the map as it was; only a failed pin load clears it.
      if (needPins) setPins([], -1);
      setSearchError(true);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        setLoadingPins(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVersion]);

  useEffect(() => {
    runSearch();
  }, [runSearch]);

  // ---- Infinite scroll: fetch and append the next page ------------------
  const loadMore = useCallback(async () => {
    const state = useSearchStore.getState();
    const query = pageQueryRef.current;
    if (!query || state.isLoading || state.isLoadingMore || !state.hasMore) {
      return;
    }

    const controller = new AbortController();
    loadMoreAbortRef.current = controller;

    setLoadingMore(true);
    try {
      const data = await searchProperties(
        query.formData,
        PAGE_SIZE,
        state.results.length,
        query.bounds,
        false, // skip the server COUNT on paged loads
        controller.signal
      );
      if (controller.signal.aborted) return;
      appendResults(data.results, data.total);
    } catch (err) {
      if ((err as Error).name === "AbortError") return;
      console.error("Load more failed:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [appendResults, setLoadingMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: listRef.current, rootMargin: "400px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // Re-attach when the list (and its sentinel) mounts/unmounts as results
    // and loading state change: the sentinel isn't in the DOM during the
    // initial skeleton state.
  }, [loadMore, results.length, isLoading]);

  // ---- Live filtering: auto-run the search after edits settle ----------
  const filterKey = JSON.stringify(formData);
  const didMountFilters = useRef(false);
  useEffect(() => {
    if (!didMountFilters.current) {
      didMountFilters.current = true;
      return;
    }
    // Don't fight an in-flight AI query; it sets filters itself.
    if (useSearchStore.getState().aiQuery) return;
    const t = window.setTimeout(() => {
      // An action that ran its own search for these exact filters (Clear all,
      // a city pick) already has them on screen. Checked when the timer fires,
      // so it never depends on effect order.
      const shown = pageQueryRef.current?.formData;
      const current = useSearchStore.getState().formData;
      if (shown && JSON.stringify(shown) === JSON.stringify(current)) return;
      submitSearch();
    }, FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [filterKey, submitSearch]);

  // On initial mount, kick off a natural-language (?q=...) search if present.
  // Structured filter params (location, beds, minPrice, propertyTypes, ...) are
  // applied to the store BEFORE mount (see buildInitialFormData in the store),
  // so the first search already reflects a shared/saved-search link with no
  // post-mount re-trigger needed.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("q");
    if (q) useSearchStore.getState().runAiQuery(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Pin click → bring that listing's card into view -------------------
  useEffect(() => {
    if (!activePropertyId || !listRef.current) return;
    const selector = `[data-listing-id="${activePropertyId.replace(/["\\]/g, "\\$&")}"]`;
    const card = listRef.current.querySelector<HTMLElement>(selector);
    card?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activePropertyId]);

  const sort = formData.sort;
  const showInitialSkeletons = isLoading && results.length === 0;
  const scopedToMap = resultsScoped;
  // Listings the server matched but could not place on the map (no
  // coordinates). Unscoped only: an area search can't contain them, and its
  // total isn't comparable with the whole-set pin count.
  const unmapped =
    !scopedToMap && pinsTotal >= 0 && totalResults > pinsTotal ? totalResults - pinsTotal : 0;
  const showingPartial = results.length < totalResults;
  // An empty map area: the pins still hold the whole filter set, so say how
  // many matches sit outside the view.
  const emptyAreaHint =
    pinsTotal === 1
      ? "1 property matches elsewhere. Zoom out to see it."
      : pinsTotal > 1
        ? `${formatCount(pinsTotal)} match elsewhere. Zoom out to see them.`
        : "Try zooming out, or widening your price range.";

  return (
    <section className={styles.results} aria-label="Search results">
      <header className={styles.header}>
        <div className={styles.count} ref={countRef} tabIndex={-1} aria-live="polite">
          {showInitialSkeletons ? (
            "Searching…"
          ) : searchError ? (
            "Search unavailable"
          ) : (
            <>
              <span className={styles.countMain}>
                {formatCount(totalResults)}
                {scopedToMap && <span className={styles.countScope}> in map area</span>}
              </span>
              {(showingPartial || unmapped > 0 || scopedToMap) && (
                <span className={styles.countSub}>
                  {showingPartial && `Showing ${results.length.toLocaleString("en-US")}`}
                  {showingPartial && (unmapped > 0 || scopedToMap) && " · "}
                  {unmapped > 0 && `${unmapped.toLocaleString("en-US")} not on map`}
                  {scopedToMap && (
                    <button
                      type="button"
                      className={styles.countAction}
                      onClick={() => {
                        // The button unmounts once the whole-set results
                        // land. Park focus on the count (a polite live
                        // region) so keyboard users keep their place.
                        countRef.current?.focus();
                        clearMapArea();
                      }}
                      aria-label="Show all matching properties, not just the map area"
                    >
                      Show all
                    </button>
                  )}
                </span>
              )}
            </>
          )}
        </div>
        <label className={styles.sort}>
          <span className={styles.sortLabel}>Sort:</span>
          <select value={sort} onChange={(e) => setField("sort", e.target.value as Sort)}>
            {(Object.keys(sortLabels) as Sort[]).map((v) => (
              <option key={v} value={v}>
                {sortLabels[v]}
              </option>
            ))}
          </select>
        </label>
      </header>

      {/* Outside the scrolling list on purpose: the pill has to stay reachable,
          and at the end of an infinite-scroll list it never is. Floats over the
          list on desktop; a slim row under this header on mobile. */}
      <Disclaimer />

      {showInitialSkeletons ? (
        <div className={styles.list} aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : searchError ? (
        <div className={styles.empty} role="alert">
          <p>We couldn&rsquo;t load listings just now.</p>
          <p className={styles.emptyHint}>Check your connection and try again.</p>
          <button type="button" className={styles.retry} onClick={submitSearch}>
            Try again
          </button>
        </div>
      ) : !isLoading && results.length === 0 ? (
        <div className={styles.empty}>
          <p>
            {scopedToMap
              ? "No matches in this map area."
              : "No properties match the current filters."}
          </p>
          <p className={styles.emptyHint}>
            {scopedToMap
              ? emptyAreaHint
              : "Try widening your price range, clearing property types, or removing a location."}
          </p>
        </div>
      ) : (
        <div
          ref={listRef}
          className={`${styles.list} ${isLoading ? styles.listRefreshing : ""}`}
          aria-busy={isLoading}
        >
          {results.map((property: PropertyResult) => (
            <PropertyCard key={property.id} property={property} />
          ))}

          {isLoadingMore &&
            Array.from({ length: 2 }).map((_, i) => <SkeletonCard key={`more-${i}`} />)}

          <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />

          {!hasMore && results.length > 0 && (
            <div className={styles.endOfList}>You&rsquo;ve reached the end of the results.</div>
          )}
        </div>
      )}
    </section>
  );
}
